/**
 * TrustGrid Scans - View scan history with drill-down
 * Refactored to use framework components (ScanList, ScanDetailModal, EventTable)
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Modal, Space, Alert } from 'antd';
import { useTrustGrid } from '../../../contexts/TrustGridContext';
import { getAllScans, getScanHistory, getScanSignals, getEventsByIds } from '../api/trustgridApi';
import { showError } from '../../../utils/notifications';
import TrustGridPage from '../../../components/layout/TrustGridPage';
import ProjectSelector from './ProjectSelector';
import ScanList from './ScanList';
import ScanDetailModal from './ScanDetailModal';
import EventTable from './EventTable';
import ScanFilterBar from './ScanFilterBar';
import { handleCrossOrgError } from '../utils/errorHandlers';

function Scans() {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedProjectId, selectProject } = useTrustGrid();

  // Build breadcrumbs from navigation state or use defaults
  const breadcrumbs = useMemo(() => {
    const baseBreadcrumbs = location.state?.breadcrumbs || [
      { label: 'TrustGrid', path: '/trustgrid/dashboard' },
    ];
    return [...baseBreadcrumbs, { label: 'Scans', path: '/trustgrid/scans' }];
  }, [location.state]);

  // Scans state
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filter state
  const [dateRange, setDateRange] = useState(null);

  // Scan detail modal state
  const [selectedScan, setSelectedScan] = useState(null);
  const [scanSignals, setScanSignals] = useState([]);
  const [loadingSignals, setLoadingSignals] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [signalStatusFilter, setSignalStatusFilter] = useState(null);

  // Evidence modal state
  const [evidenceEvents, setEvidenceEvents] = useState([]);
  const [loadingEvidence, setLoadingEvidence] = useState(false);
  const [evidenceModalVisible, setEvidenceModalVisible] = useState(false);
  const [selectedSignal, setSelectedSignal] = useState(null);

  // Fetch scans on project change
  useEffect(() => {
    fetchScans();
  }, [selectedProjectId]);

  /**
   * Fetch ALL scans for current project or all projects (client-side pagination)
   */
  const fetchScans = async () => {
    setLoading(true);
    try {
      let response;
      if (selectedProjectId && selectedProjectId !== 'all') {
        // Fetch ALL scans for specific project
        response = await getScanHistory(selectedProjectId, 1, 10000, {
          sort_by: 'started_at',
          sort_order: 'desc',
        });
      } else {
        // Fetch ALL scans across all projects in user's org
        response = await getAllScans(1, 10000, {
          sort_by: 'started_at',
          sort_order: 'desc',
        });
      }
      setScans(response.data.scans || []);
    } catch (error) {
      const wasHandled = handleCrossOrgError(error, selectProject, navigate, 'Scans');
      if (!wasHandled) {
        console.error('Failed to fetch scans:', error);
        showError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle view scan details (drill-down)
   */
  const handleViewScanDetails = async (scan, statusFilter = null) => {
    setSelectedScan(scan);
    setSignalStatusFilter(statusFilter);
    setDetailModalVisible(true);
    setLoadingSignals(true);

    try {
      const filters = statusFilter ? { status: statusFilter } : {};
      const response = await getScanSignals(scan.id, filters);
      setScanSignals(response.data.signals || []);
    } catch (error) {
      const wasHandled = handleCrossOrgError(error, selectProject, navigate, 'Scans');
      if (!wasHandled) {
        console.error('Failed to fetch scan signals:', error);
        showError(error);
      }
      setScanSignals([]);
    } finally {
      setLoadingSignals(false);
    }
  };

  /**
   * Handle clear signal status filter
   */
  const handleClearSignalFilter = () => {
    if (selectedScan) {
      setSignalStatusFilter(null);
      handleViewScanDetails(selectedScan, null);
    }
  };

  /**
   * Handle view evidence (from signal)
   */
  const handleViewEvidence = async (signal) => {
    setSelectedSignal(signal);
    setEvidenceModalVisible(true);
    setLoadingEvidence(true);

    try {
      // Parse evidence_refs (JSON array of event IDs)
      const refs = signal.evidence_refs;
      let eventIds = [];

      if (refs) {
        try {
          eventIds = typeof refs === 'string' ? JSON.parse(refs) : refs;
        } catch {
          eventIds = [];
        }
      }

      if (eventIds.length === 0) {
        setEvidenceEvents([]);
        setLoadingEvidence(false);
        return;
      }

      // Fetch events by IDs
      const response = await getEventsByIds(eventIds);
      setEvidenceEvents(response.data.events || []);
    } catch (error) {
      const wasHandled = handleCrossOrgError(error, selectProject, navigate, 'Scans');
      if (!wasHandled) {
        console.error('Failed to fetch evidence events:', error);
        showError(error);
      }
      setEvidenceEvents([]);
    } finally {
      setLoadingEvidence(false);
    }
  };

  /**
   * Fetch all evidence events for export (called by TGDataTable in EventTable)
   */
  const fetchAllEvidence = async () => {
    // Evidence is already fully loaded since we fetch by IDs
    // No need to make another API call
    return evidenceEvents;
  };

  /**
   * Fetch all scan signals for export (called by TGDataTable in ScanDetailModal)
   */
  const fetchAllSignals = async () => {
    // Signals are already fully loaded
    // No need to make another API call
    return scanSignals;
  };

  /**
   * Close evidence modal
   */
  const handleCloseEvidenceModal = () => {
    setEvidenceModalVisible(false);
    setSelectedSignal(null);
    setEvidenceEvents([]);
  };

  /**
   * Close scan detail modal
   */
  const handleCloseScanDetailModal = () => {
    setDetailModalVisible(false);
    setSelectedScan(null);
    setScanSignals([]);
    setSignalStatusFilter(null);
  };

  /**
   * Filter scans by date range
   */
  const filteredScans = useMemo(() => {
    if (!dateRange || dateRange.length !== 2) return scans;

    const [startDate, endDate] = dateRange;
    const startTime = startDate ? new Date(startDate).setHours(0, 0, 0, 0) : null;
    const endTime = endDate ? new Date(endDate).setHours(23, 59, 59, 999) : null;

    return scans.filter((scan) => {
      if (!scan.started_at) return false;
      const scanTime = new Date(scan.started_at).getTime();

      if (startTime && scanTime < startTime) return false;
      if (endTime && scanTime > endTime) return false;

      return true;
    });
  }, [scans, dateRange]);

  /**
   * Clear all filters
   */
  const handleClearFilters = () => {
    setDateRange(null);
  };

  return (
    <TrustGridPage
      title="Scans"
      subtitle="View scan history and control evaluation results"
      breadcrumbs={breadcrumbs}
      showRefresh
      onRefresh={fetchScans}
    >
      <ProjectSelector />

      <ScanList
        data={filteredScans}
        loading={loading}
        onViewDetails={handleViewScanDetails}
        onRefresh={fetchScans}
        showProjectColumn={!selectedProjectId}
        externalFilterCount={dateRange && dateRange.length === 2 ? 1 : 0}
        onClearExternalFilters={handleClearFilters}
        toolbar={
          <ScanFilterBar
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
          />
        }
      />

      {/* Scan Detail Modal with Signals Table */}
      <ScanDetailModal
        visible={detailModalVisible}
        scan={selectedScan}
        signals={scanSignals}
        loading={loadingSignals}
        statusFilter={signalStatusFilter}
        onClose={handleCloseScanDetailModal}
        onViewEvidence={handleViewEvidence}
        onClearFilter={handleClearSignalFilter}
        fetchAllSignals={fetchAllSignals}
      />

      {/* Evidence Events Modal */}
      <Modal
        title={
          selectedSignal ? (
            <Space>
              <span>Evidence for {selectedSignal.control_code}</span>
              <span style={{ fontSize: 14, fontWeight: 'normal', color: '#888' }}>
                ({evidenceEvents.length} of {selectedSignal.evidence_count} events)
              </span>
            </Space>
          ) : (
            'Evidence Events'
          )
        }
        open={evidenceModalVisible}
        onCancel={handleCloseEvidenceModal}
        footer={null}
        width={1400}
        destroyOnClose
      >
        {selectedSignal && evidenceEvents.length < selectedSignal.evidence_count && (
          <Alert
            message="Limited Evidence Available"
            description={`Showing ${evidenceEvents.length} of ${selectedSignal.evidence_count} total events. This scan was created with a system limit on stored evidence references. Re-run the scan to capture all event references for complete traceability.`}
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            closable
          />
        )}
        <EventTable
          data={evidenceEvents}
          loading={loadingEvidence}
          fetchAll={fetchAllEvidence}
        />
      </Modal>
    </TrustGridPage>
  );
}

export default Scans;
